import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import { useNavigate } from 'react-router-dom';
import remarkGfm from 'remark-gfm';
import { 
  ArrowLeft, 
  ArrowRight, 
  Clock, 
  Calendar, 
  Car, 
  Tag, 
  HashIcon, 
  Settings, 
  Palette, 
  MapPin,
  MessageCircle,
  Users,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

const DashboardContainer = styled.div`
  height: 100vh;
  padding: 0;
  overflow: hidden;
  width: 90%;
  margin: 0 auto;
`;

const DashboardHeader = styled.header`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding: 1rem 2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 80px;
  flex-shrink: 0;
  
  @media (max-width: 992px) {
    padding: 1rem;
  }
  @media (max-width: 768px) {
    padding: 12px;
    flex-direction: column;
    gap: 1rem;
    height: auto;
    min-height: 80px;
  }
`;

const HeaderTitle = styled.h1`
  color: white;
  font-size: 24px;
  font-weight: 500;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  

  @media (max-width: 992px) {
    font-size: 18px;
  }
`;

const HeaderStats = styled.div`
  display: flex;
  gap: 2rem;
  align-items: center;
  
  @media (max-width: 768px) {
    gap: 1rem;
    flex-wrap: wrap;
    justify-content: center;
  }
`;

const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: white;
  font-weight: 500;
  
  .stat-number {
    background: rgba(255, 255, 255, 0.2);
    padding: 0.25rem 0.75rem;
    border-radius: 20px;
    font-weight: 700;
    font-size: 0.9rem;
  }
`;

const DashboardContent = styled.div`
  display: grid;
  grid-template-columns: ${props => props.isCollapsed ? '80px' : '320px'} 1fr;
  gap: 0;
  height: calc(100vh - 80px);
  transition: grid-template-columns 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  @media (max-width: 1024px) {
    grid-template-columns: ${props => props.isCollapsed ? '80px' : '280px'} 1fr;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    height: calc(100vh - 120px);
  }
`;

const SessionsPanel = styled.div`
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(20px);
  border-right: 1px solid rgba(255, 255, 255, 0.1);
  height: 100%;
  overflow: hidden;
  position: relative;
  display: flex;
  flex-direction: column;
  
  @media (max-width: 768px) {
    position: fixed;
    top: 0;
    left: 0;
    width: 280px;
    height: 100vh;
    z-index: 1000;
    transform: ${props => props.isCollapsed ? 'translateX(-100%)' : 'translateX(0)'};
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    border-right: 1px solid rgba(255, 255, 255, 0.1);
    overflow: visible;
  }
`;



const SessionsContent = styled.div`
  padding: 1.5rem;
  flex: 1;
  overflow-y: auto;
  opacity: ${props => props.isCollapsed ? 0 : 1};
  transform: ${props => props.isCollapsed ? 'translateX(-20px)' : 'translateX(0)'};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  pointer-events: ${props => props.isCollapsed ? 'none' : 'auto'};
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.3);
    border-radius: 3px;
  }
  
  @media (max-width: 768px) {
    padding: 1rem;
    opacity: 1;
    transform: none;
    pointer-events: auto;
  }
`;

const SessionsHeader = styled.div`
  margin-bottom: 1.5rem;
  
  h2 {
    color: white;
    font-size: 20px;
    font-weight: 600;
    margin: 0 0 0.5rem 0;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  
  .session-count {
    background: linear-gradient(135deg, #F43F60, #FF6B9D);
    color: white;
    padding: 0.25rem 0.75rem;
    border-radius: 20px;
    font-size: 0.8rem;
    font-weight: 600;
    backdrop-filter: blur(10px);
    box-shadow: 0 2px 8px rgba(244, 63, 96, 0.3);
  }
  
  @media (max-width: 768px) {
    margin-bottom: 1rem;
    
    h2 {
      font-size: 1.1rem;
    }
  }
`;

const SessionItem = styled.div`
  background: ${props => props.active 
    ? 'linear-gradient(135deg, rgba(244, 63, 96, 0.2), rgba(244, 63, 96, 0.1))' 
    : 'rgba(255, 255, 255, 0.05)'};
  border-radius: 12px;
  padding: 1rem;
  margin-bottom: 0.75rem;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: ${props => props.active 
      ? 'linear-gradient(90deg, #F43F60, #FF6B9D)' 
      : 'transparent'};
    transition: all 0.3s ease;
  }
  
  &:hover {
    background: ${props => props.active 
      ? 'linear-gradient(135deg, rgba(244, 63, 96, 0.25), rgba(244, 63, 96, 0.15))' 
      : 'rgba(255, 255, 255, 0.08)'};
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  }
  
  @media (max-width: 768px) {
    padding: 0.75rem;
    margin-bottom: 0.5rem;
  }
`;

const SessionItemHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  
  .session-id {
    color: white;
    font-weight: 500;
    font-size: 0.9rem;
    font-family: 'Monaco', 'Menlo', monospace;
  }
  
  .message-count {
    background: rgba(255, 255, 255, 0.2);
    color: white;
    padding: 0.2rem 0.5rem;
    border-radius: 12px;
    font-size: 0.75rem;
    font-weight: 500;
    backdrop-filter: blur(10px);
  }
`;

const SessionContent = styled.div`
  .last-message {
    color: rgba(255, 255, 255, 0.8);
    font-size: 16px;
    line-height: 1.3;
    margin-bottom: 15px;
    font-weight: ${props => props.active ? '500' : '400'};
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  
  .session-meta {
    display: flex;
    gap: 1rem;
    flex-wrap: wrap;
    
    span {
      display: flex;
      align-items: center;
      gap: 0.25rem;
      color: rgba(255, 255, 255, 0.6);
      font-size: 0.75rem;
      padding: 0.2rem 0.5rem;
      background: rgba(255, 255, 255, 0.1);
      border-radius: 8px;
      backdrop-filter: blur(10px);
      transition: all 0.2s ease;
      
      &:hover {
        background: rgba(255, 255, 255, 0.15);
        color: rgba(255, 255, 255, 0.8);
      }
    }
  }
`;

const CollapsedIndicator = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) rotate(-90deg);
  color: white;
  font-weight: 600;
  font-size: 0.9rem;
  opacity: ${props => props.isCollapsed ? 1 : 0};
  transition: opacity 0.3s ease;
  white-space: nowrap;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const MessagesPanel = styled.div`
  background: rgba(255, 255, 255, 0.02);
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  
  @media (max-width: 768px) {
    height: auto;
    flex: 1;
  }
`;

const MessagesHeader = styled.div`
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding: 1.5rem 2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  
  h2 {
    color: white;
    font-size: 20px;
    font-weight: 500;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  
  .back-button {
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.2);
    color: white;
    padding: 0.5rem 1rem;
    border-radius: 8px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.9rem;
    font-weight: 500;
    transition: all 0.2s ease;
    backdrop-filter: blur(10px);
    
    &:hover {
      background: rgba(255, 255, 255, 0.15);
      transform: translateY(-1px);
    }
  }
  
  @media (max-width: 768px) {
    padding: 1rem;
    flex-direction: column;
    gap: 1rem;
    align-items: flex-start;
    
    h2 {
      font-size: 1.1rem;
    }
    
    .back-button {
      width: 100%;
      justify-content: center;
    }
  }
`;

const MessagesContainer = styled.div`
  flex: 1;
  padding: 2rem;
  overflow-y: auto;
  min-height: 0;
  
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 4px;
  }
  
  @media (max-width: 768px) {
    padding: 1rem;
    max-height: calc(100vh - 300px);
  }
`;

const MessageBubble = styled.div`
  background: ${props => props.isuser === 'true' 
    ? 'linear-gradient(135deg, rgba(244, 63, 96, 0.15), rgba(244, 63, 96, 0.1))' 
    : 'rgba(255, 255, 255, 0.05)'};
  border-radius: 16px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  position: relative;
  backdrop-filter: blur(10px);
  width: 100%;
  max-width: max-content;
  margin-left: ${props => props.isuser === 'true' ? 'auto' : '0'};
  
  &::before {
    content: '${props => props.isuser === 'true' ? 'You' : 'AI Assistant'}';
    position: absolute;
    top: -8px;
    left: 1rem;
    background: ${props => props.isuser === 'true' 
      ? 'linear-gradient(135deg, #F43F60, #FF6B9D)' 
      : 'linear-gradient(135deg, #667eea, #764ba2)'};
    color: white;
    padding: 0.25rem 0.75rem;
    border-radius: 12px;
    font-size: 0.75rem;
    font-weight: 600;
    backdrop-filter: blur(10px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  }
  
  p {
    color: white;
    line-height: 1.6;
    margin: 0 0 1rem 0;
    font-size: 1rem;
    
    &:last-child {
      margin-bottom: 0;
    }
  }

  h2 {
    font-size: 20px;
    line-height: 1.2;
    margin-bottom: 0.75rem;
  }
  ul, ol {
    margin-bottom: 10px;
  }
  
  @media (max-width: 768px) {
    padding: 1rem;
    margin-bottom: 1rem;
  }
`;

const MessageAttachments = styled.div`
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  
  .attachment-title {
    color: rgba(255, 255, 255, 0.8);
    font-size: 0.9rem;
    font-weight: 600;
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
`;

const CarCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1rem;
  margin-bottom: 1rem;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(244, 63, 96, 0.1), rgba(244, 63, 96, 0.05));
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
    border-color: rgba(244, 63, 96, 0.3);
    
    &::before {
      opacity: 1;
    }
    
    img {
      transform: scale(1.05);
    }
  }
  
  @media (max-width: 768px) {
    padding: 0.75rem;
    margin-bottom: 0.75rem;
  }
`;

const CarCardContent = styled.div`
  display: flex;
  gap: 1rem;
  align-items: flex-start;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.75rem;
  }
`;

const CarImageWrapper = styled.div`
  flex-shrink: 0;
  width: 120px;
  height: 90px;
  border-radius: 8px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.1);
  
  @media (max-width: 768px) {
    width: 100%;
    height: 150px;
  }
`;

const CarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
`;

const CarDetails = styled.div`
  flex: 1;
  min-width: 0;
`;

const CarTitle = styled.h3`
  color: white;
  font-size: 1.1rem;
  font-weight: 700;
  margin: 0 0 0.5rem 0;
  line-height: 1.3;
`;

const CarInfo = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 0.5rem;
  margin-bottom: 0.75rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.25rem;
  }
`;

const CarInfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.85rem;
  padding: 0.25rem 0.5rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 6px;
  backdrop-filter: blur(10px);
`;

const CarPrice = styled.div`
  color: #F43F60;
  font-size: 1.2rem;
  font-weight: 700;
  margin-bottom: 0.25rem;
`;

const CarMileage = styled.div`
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.85rem;
  margin-bottom: 0.5rem;
`;

const CarLocation = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.8rem;
`;

const CarBadge = styled.span`
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  background: linear-gradient(135deg, #F43F60, #FF6B9D);
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  backdrop-filter: blur(10px);
`;

const CarStatus = styled.span`
  position: absolute;
  top: 0.75rem;
  left: 0.75rem;
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  backdrop-filter: blur(10px);
`;

const OfferCard = styled.div`
  background: linear-gradient(135deg, rgba(244, 63, 96, 0.1), rgba(244, 63, 96, 0.05));
  border: 1px solid rgba(244, 63, 96, 0.2);
  border-radius: 12px;
  padding: 1rem;
  
  .offer-title {
    color: #F43F60;
    font-weight: 700;
    font-size: 1rem;
    margin-bottom: 0.5rem;
  }
  
  .offer-details {
    color: rgba(255, 255, 255, 0.8);
    font-size: 0.9rem;
    line-height: 1.5;
  }
`;

const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.75rem;
  }
`;

const PaginationButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.2s ease;
  backdrop-filter: blur(10px);
  
  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.15);
    transform: translateY(-1px);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PageIndicator = styled.span`
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.9rem;
  font-weight: 500;
  padding: 0.5rem 1rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  backdrop-filter: blur(10px);
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 2rem;
  color: white;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  
  .empty-icon {
    font-size: 3rem;
    margin-bottom: 1rem;
    opacity: 0.7;
  }
  
  h3 {
    font-size: 24px;
    font-weight: 600;
    margin: 0 0 0.5rem 0;
  }
  
  p {
    color: rgba(255, 255, 255, 0.7);
    font-size: 1rem;
    margin: 0;
  }
  
  @media (max-width: 768px) {
    padding: 1rem;
    
    .empty-icon {
      font-size: 2.5rem;
    }
    
    h3 {
      font-size: 1.3rem;
    }
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  
  .loading-text {
    color: white;
    font-size: 1.1rem;
    font-weight: 500;
  }
`;

const MobileToggleButton = styled.button`
  display: none;
  position: absolute;
  top: 50%;
  right: -38px;
  transform: translateY(-50%);
  background: linear-gradient(135deg, #F43F60, #FF6B9D);
  border: none;
  color: white;
  width: 38px;
  height: 80px;
  border-radius: 0px 12px 12px 0px;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1001;
  box-shadow: 0 8px 24px rgba(244, 63, 96, 0.4);
  
  &:hover {
    transform: translateY(-50%) scale(1.1);
    box-shadow: 0 12px 32px rgba(244, 63, 96, 0.5);
  }
  
  &:active {
    transform: translateY(-50%) scale(0.95);
  }
  
  svg {
    transition: transform 0.3s ease;
    transform: ${props => props.isCollapsed ? 'rotate(180deg)' : 'rotate(0deg)'};
    width: 20px;
    height: 20px;
  }
  
  @media (max-width: 768px) {
    display: flex;
  }
`;

const CollapseButton = styled.button`
  position: absolute;
  top: 50%;
  right: 0px;
  transform: translateY(-50%);
  background: linear-gradient(135deg, #F43F60, #FF6B9D);
  border: none;
  color: white;
  width: 27px;
  height: 80px;
  border-radius: 12px 0 0 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 20;
  box-shadow: 0 4px 16px rgba(244, 63, 96, 0.3);
  
  &:hover {
    transform: translateY(-50%) scale(1.05);
    box-shadow: 0 6px 20px rgba(244, 63, 96, 0.4);
  }
  
  &:active {
    transform: translateY(-50%) scale(0.95);
  }
  
  svg {
    transition: transform 0.3s ease;
    transform: ${props => props.isCollapsed ? 'rotate(180deg)' : 'rotate(0deg)'};
    width: 20px;
    height: 20px;
  }
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileOverlay = styled.div`
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
  opacity: ${props => props.isCollapsed ? 0 : 1};
  visibility: ${props => props.isCollapsed ? 'hidden' : 'visible'};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const UserDashboard = () => {
  const [sessions, setSessions] = useState([]);
  const [totalSessions, setTotalSessions] = useState(0);
  const [selectedSession, setSelectedSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [error, setError] = useState(null);
  const [carPagination, setCarPagination] = useState({});
  const [isCollapsed, setIsCollapsed] = useState(false);
  const navigate = useNavigate();

  // Pagination settings
  const CARS_PER_PAGE = 6;

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  useEffect(() => {
    fetchSessions();
  }, []);

  useEffect(() => {
    if (selectedSession) {
      fetchSessionMessages(selectedSession.sessionId);
      resetPagination(); // Reset pagination when switching sessions
    }
  }, [selectedSession]);

  // Initialize pagination for a message
  const initializePagination = (messageIndex, totalCars) => {
    setCarPagination(prev => ({
      ...prev,
      [messageIndex]: {
        currentPage: 1,
        totalPages: Math.ceil(totalCars / CARS_PER_PAGE),
        totalCars
      }
    }));
  };

  // Get paginated cars for a specific message
  const getPaginatedCars = (cars, messageIndex) => {
    const pagination = carPagination[messageIndex];
    if (!pagination) {
      initializePagination(messageIndex, cars.length);
      return cars.slice(0, CARS_PER_PAGE);
    }
    
    const startIndex = (pagination.currentPage - 1) * CARS_PER_PAGE;
    const endIndex = startIndex + CARS_PER_PAGE;
    return cars.slice(startIndex, endIndex);
  };

  // Handle page change
  const handlePageChange = (messageIndex, newPage) => {
    setCarPagination(prev => ({
      ...prev,
      [messageIndex]: {
        ...prev[messageIndex],
        currentPage: newPage
      }
    }));
  };

  // Reset pagination when switching sessions
  const resetPagination = () => {
    setCarPagination({});
  };

  const handleSessionSelect = (session) => {
    setSelectedSession(session);
    // Auto close mobile sidebar when conversation is selected
    if (window.innerWidth <= 768) {
      setIsCollapsed(true);
    }
  };

  const handleBackToSessions = () => {
    setSelectedSession(null);
    setMessages([]);
    resetPagination(); // Reset pagination when going back
  };

  const fetchSessions = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/messages/sessions`, {
        headers: {
          'Authorization': `Bearer ${sessionStorage.getItem('token')}`
        }
      });
      
      const data = await response.json();
      
      if (data.status === 'success') {
        // Filter out sessions with null sessionId and sort by lastMessage (newest first)
        const validSessions = data.data.sessions
          .filter(session => session.sessionId !== null)
          .sort((a, b) => new Date(b.lastMessage) - new Date(a.lastMessage));
        
        setSessions(validSessions);
        setTotalSessions(data.data.totalSessions);
        if (validSessions.length > 0) {
          setSelectedSession(validSessions[0]);
        }
      } else {
        throw new Error(data.message || 'Failed to fetch sessions');
      }
    } catch (err) {
      console.error('Error fetching sessions:', err);
      setError('Failed to load chat sessions');
    } finally {
      setLoading(false);
    }
  };

  const fetchSessionMessages = async (sessionId) => {
    try {
      setLoadingMessages(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/messages/sessions/${sessionId}`, {
        headers: {
          'Authorization': `Bearer ${sessionStorage.getItem('token')}`
        }
      });
      
      const data = await response.json();
      
      if (data.status === 'success') {
        setMessages(data.data.messages || []);
      } else {
        throw new Error(data.message || 'Failed to fetch messages');
      }
    } catch (err) {
      console.error('Error fetching messages:', err);
      setError('Failed to load messages');
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleCarClick = (car) => {
    if (car._id) {
      navigate(`/car-details?id=${car._id}`);
    }
  };

  const formatSessionId = (sessionId) => {
    if (!sessionId) return 'Unknown';
    // For UUID format, show first 8 characters
    return sessionId.substring(0, 8) + '...';
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);
    
    if (diffInHours < 1) {
      return 'Just now';
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`;
    } else if (diffInHours < 168) { // 7 days
      return `${Math.floor(diffInHours / 24)}d ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  if (loading) {
    return (
      <DashboardContainer>
        <LoadingContainer>
          <div className="loading-text">Loading chat sessions...</div>
        </LoadingContainer>
      </DashboardContainer>
    );
  }

  if (error) {
    return (
      <DashboardContainer>
        <EmptyState>
          <div className="empty-icon">⚠️</div>
          <h3>Error Loading Sessions</h3>
          <p>{error}</p>
        </EmptyState>
      </DashboardContainer>
    );
  }

  return (
    <DashboardContainer>
      <DashboardHeader>
        <HeaderTitle>
          <MessageCircle size={24} />
          User Dashboard
        </HeaderTitle>
        <HeaderStats>
          <StatItem>
            <Users size={16} />
            Total Sessions: <span className="stat-number">{totalSessions}</span>
          </StatItem>
          <StatItem>
          </StatItem>
          <button 
            onClick={() => navigate('/user/chats')}
            style={{
              background: 'rgba(244, 63, 96, 0.1)',
              border: '1px solid rgba(244, 63, 96, 0.3)',
              color: '#fff',
              padding: '0.5rem 1rem',
              borderRadius: '8px',
              fontSize: '15px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = 'rgba(244, 63, 96, 0.2)';
              e.target.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'rgba(244, 63, 96, 0.1)';
              e.target.style.transform = 'translateY(0)';
            }}
          >
            View Dealers Chat History
          </button>
        </HeaderStats>
      </DashboardHeader>

      <DashboardContent isCollapsed={isCollapsed}>
        <SessionsPanel isCollapsed={isCollapsed}>
          <CollapseButton isCollapsed={isCollapsed} onClick={toggleCollapse}>
            <ChevronLeft size={18} />
          </CollapseButton>
          <SessionsContent isCollapsed={isCollapsed}>
            <SessionsHeader>
              <h2>
                <MessageCircle size={20} />
                Conversations
                <span className="session-count">{totalSessions}</span>
              </h2>
            </SessionsHeader>
            
            {sessions.length === 0 ? (
              <EmptyState>
                <div className="empty-icon">💬</div>
                <h3>No Conversations Yet</h3>
                <p>Start chatting with our AI assistant to see your conversation history here.</p>
              </EmptyState>
            ) : (
              <AnimatePresence>
  {/* Mobile Toggle Button */}
  <MobileToggleButton isCollapsed={isCollapsed} onClick={toggleCollapse}>
        <ChevronLeft size={20} />
      </MobileToggleButton>
                {sessions.map((session, index) => (
                  <motion.div
                    key={session.sessionId}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                  >
                    <SessionItem
                      key={session.sessionId}
                      active={selectedSession?.sessionId === session.sessionId}
                      onClick={() => handleSessionSelect(session)}
                    >
                      <SessionItemHeader>
                        <span className="session-id">{formatSessionId(session.sessionId)}</span>
                        <span className="message-count">{session.messageCount}</span>
                      </SessionItemHeader>
                      <SessionContent active={selectedSession?.sessionId === session.sessionId}>
                        <div className="last-message">
                          {session.lastMessageContent || 'No messages yet'}
                        </div>
                        <div className="session-meta">
                          <span>
                            <Clock size={12} />
                            {formatTimestamp(session.lastMessage)}
                          </span>
                          <span>
                            <Calendar size={12} />
                            Started: {formatTimestamp(session.firstMessage)}
                          </span>
                        </div>
                      </SessionContent>
                    </SessionItem>
                  </motion.div>
                ))}
              </AnimatePresence>
            )}
          </SessionsContent>
          <CollapsedIndicator isCollapsed={isCollapsed}>Conversations</CollapsedIndicator>
        </SessionsPanel>
        
        <MessagesPanel>
          {selectedSession ? (
            <>
              <MessagesHeader>
                <h2>
                  <MessageCircle size={20} />
                  Messages
                </h2>
                <button className="back-button" onClick={handleBackToSessions}>
                  <ArrowLeft size={16} />
                  Back to Sessions
                </button>
              </MessagesHeader>
              
              <MessagesContainer>
                {loadingMessages ? (
                  <LoadingContainer>
                    <div className="loading-text">Loading messages...</div>
                  </LoadingContainer>
                ) : messages.length === 0 ? (
                  <EmptyState>
                    <div className="empty-icon">💬</div>
                    <h3>No Messages</h3>
                    <p>This conversation doesn't have any messages yet.</p>
                  </EmptyState>
                ) : (
                  <AnimatePresence>
                    {messages.map((message, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, x: message.type === 'user' ? 20 : -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.1 }}
                      >
                        <MessageBubble
                          isuser={message.type === 'user' ? 'true' : 'false'}
                        >
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {message.content}
                          </ReactMarkdown>
                          
                          {message.vehicles && message.vehicles.length > 0 && (
                            <MessageAttachments>
                              <div className="attachment-title">
                                <Car size={14} />
                                Car Search Results ({message.vehicles.length})
                              </div>
                              {getPaginatedCars(message.vehicles, index).map((car, carIndex) => (
                                <CarCard key={carIndex} onClick={() => handleCarClick(car)}>
                                  {car.condition && (
                                    <CarBadge>
                                      {car.condition}
                                    </CarBadge>
                                  )}
                                  {car.status && (
                                    <CarStatus>
                                      {car.status}
                                    </CarStatus>
                                  )}
                                  <CarCardContent>
                                    <CarImageWrapper>
                                      <CarImage 
                                        src={car.images?.[0]?.url || car.imageUrl || '/placeholder-car.jpg'} 
                                        alt={`${car.year} ${car.make} ${car.model}`}
                                        onError={(e) => {
                                          e.target.src = '/placeholder-car.jpg';
                                        }}
                                      />
                                    </CarImageWrapper>
                                    <CarDetails>
                                      <CarTitle>{car.year} {car.make} {car.model}</CarTitle>
                                      <CarInfo>
                                        {car.trim && (
                                          <CarInfoRow>
                                            <Tag size={14} />
                                            {car.trim}
                                          </CarInfoRow>
                                        )}
                                        {car.stockNumber && (
                                          <CarInfoRow>
                                            <HashIcon size={14} />
                                            Stock: {car.stockNumber}
                                          </CarInfoRow>
                                        )}
                                        {car.transmission && (
                                          <CarInfoRow>
                                            <Settings size={14} />
                                            {car.transmission}
                                          </CarInfoRow>
                                        )}
                                        {car.engine && (
                                          <CarInfoRow>
                                            <Car size={14} />
                                            {car.engine}
                                          </CarInfoRow>
                                        )}
                                        {car.exteriorColor && (
                                          <CarInfoRow>
                                            <Palette size={14} />
                                            {car.exteriorColor}
                                          </CarInfoRow>
                                        )}
                                        {car.vin && (
                                          <CarInfoRow>
                                            <HashIcon size={14} />
                                            VIN: {car.vin}
                                          </CarInfoRow>
                                        )}
                                      </CarInfo>
                                      <CarPrice>${car.askingPrice?.toLocaleString()}</CarPrice>
                                      <CarMileage>{car.mileage?.toLocaleString()} miles</CarMileage>
                                      {car.dealer?.name && (
                                        <CarLocation>
                                          <MapPin size={12} />
                                          {car.dealer.name}
                                        </CarLocation>
                                      )}
                                    </CarDetails>
                                  </CarCardContent>
                                </CarCard>
                              ))}
                              {message.vehicles.length > CARS_PER_PAGE && (
                                <PaginationControls>
                                  <PaginationButton
                                    onClick={() => handlePageChange(index, carPagination[index]?.currentPage - 1)}
                                    disabled={carPagination[index]?.currentPage <= 1}
                                  >
                                    <ChevronLeft size={16} />
                                    Previous
                                  </PaginationButton>
                                  <PageIndicator>
                                    Page {carPagination[index]?.currentPage} of {carPagination[index]?.totalPages}
                                  </PageIndicator>
                                  <PaginationButton
                                    onClick={() => handlePageChange(index, carPagination[index]?.currentPage + 1)}
                                    disabled={carPagination[index]?.currentPage >= carPagination[index]?.totalPages}
                                  >
                                    Next
                                    <ChevronRight size={16} />
                                  </PaginationButton>
                                </PaginationControls>
                              )}
                            </MessageAttachments>
                          )}
                          
                          {message.offer && (
                            <MessageAttachments>
                              <div className="attachment-title">
                                <Tag size={14} />
                                Special Offer
                              </div>
                              <OfferCard>
                                <div className="offer-title">
                                  {message.offer.offer.title || 'Special Deal'}
                                </div>
                                <div className="offer-details">
                                  {message.offer.offer.description || 'Limited time offer available'}
                                </div>
                              </OfferCard>
                            </MessageAttachments>
                          )}
                        </MessageBubble>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                )}
              </MessagesContainer>
            </>
          ) : (
            <EmptyState>
              <div className="empty-icon">💬</div>
              <h3>Select a Conversation</h3>
              <p>Choose a conversation from the list to view its messages.</p>
            </EmptyState>
          )}
        </MessagesPanel>
      </DashboardContent>
      
    
      
      {/* Mobile Overlay */}
      <MobileOverlay isCollapsed={isCollapsed} onClick={toggleCollapse} />
    </DashboardContainer>
  );
};

export default UserDashboard; 